import React, { useState, useEffect, useMemo } from 'react';
import type { ColDef } from '@ag-grid-community/core';
import { Loader2, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { utils, writeFile } from 'xlsx'; // Use utils and writeFile directly
import AGDataGrid from '@/components/ag-data-grid';
import { useBreadcrumb } from '@/components/breadcrumb-provider';
import Container from '@/components/container';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input'; // Added Input for search
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import type { MarketingContact, MarketingContactsResponse, OrganizationOddity } from '@/hooks/use-reports'; // Import MarketingContactsResponse
import { useMarketingContactsReport } from '@/hooks/use-reports';

const REPORT_PATH = '/app/reports/marketing-contacts';

// Helper to format headers for display
const formatHeader = (header: string) => header.replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase());

// Column Definitions for Contacts
function contactColDefs(): ColDef<MarketingContact>[] {
  return [
    { field: 'legal_name', headerName: formatHeader('legal_name'), filter: true, sortable: true, resizable: true },
    { field: 'email', headerName: formatHeader('email'), filter: true, sortable: true, resizable: true },
    { field: 'phone_number', headerName: formatHeader('phone_number'), filter: true, sortable: true, resizable: true },
    { field: 'organization_legal_name', headerName: formatHeader('organization_legal_name'), filter: true, sortable: true, resizable: true },
    { field: 'country', headerName: formatHeader('country'), filter: true, sortable: true, resizable: true },
    { field: 'state_province', headerName: formatHeader('state_province'), filter: true, sortable: true, resizable: true },
    { field: 'domains', headerName: formatHeader('domains'), valueFormatter: (params) => Array.isArray(params.value) ? params.value.join(', ') : params.value, filter: true, sortable: true, resizable: true },
    { field: 'type', headerName: formatHeader('type'), filter: true, sortable: true, resizable: true },
    { field: 'us_bank', headerName: 'US Bank', filter: true, sortable: true, resizable: true },
    { field: 'fdic_licenses', headerName: 'FDIC Licenses', filter: true, sortable: true, resizable: true },
    { field: 'holding_company_licenses', headerName: 'Holding Company Licenses', filter: true, sortable: true, resizable: true },
    { field: 'other_licenses', headerName: 'Other Licenses', filter: true, sortable: true, resizable: true },
    { field: 'use_restriction', headerName: 'Use Restriction', filter: true, sortable: true, resizable: true },
    { field: 'use_restriction_note', headerName: 'Use Restriction Note', filter: true, sortable: true, resizable: true },
  ];
}

// Column Definitions for Oddities
function oddityColDefs(): ColDef<OrganizationOddity>[] {
  return [
    { field: 'name', headerName: formatHeader('name'), filter: true, sortable: true, resizable: true },
    { field: 'hasRegistrant', headerName: formatHeader('hasRegistrant'), filter: 'agBooleanColumnFilter', sortable: true, resizable: true, valueFormatter: (params) => params.value ? '✅' : '❌' }, // Added emoji formatter
    { field: 'hasVerification', headerName: formatHeader('hasVerification'), filter: 'agBooleanColumnFilter', sortable: true, resizable: true, valueFormatter: (params) => params.value ? '✅' : '❌' }, // Added emoji formatter
    { field: 'registrantEmail', headerName: formatHeader('registrantEmail'), filter: true, sortable: true, resizable: true },
    { field: 'verificationEmail', headerName: formatHeader('verificationEmail'), filter: true, sortable: true, resizable: true },
  ];
}

// Define headers explicitly for Excel export
const contactHeadersExcel: (keyof MarketingContact)[] = [
  'legal_name',
  'email',
  'phone_number',
  'organization_legal_name',
  'country',
  'state_province',
  'domains',
  'type',
  'us_bank',
  'fdic_licenses',
  'holding_company_licenses',
  'other_licenses',
  'use_restriction',
  'use_restriction_note',
];
const oddityHeadersExcel: (keyof OrganizationOddity)[] = [
  'name',
  'hasRegistrant',
  'hasVerification',
  'registrantEmail',
  'verificationEmail',
];

// Pre-process data for Excel: Select only desired keys and format domains array
function processContactDataForExcel(contact: MarketingContact) {
  const processed: Record<string, string | number | boolean> = {};
  for (const header of contactHeadersExcel) {
    const value = contact[header]; // Type-safe access
    // Use ternary for cleaner assignment and explicitly cast the result
    processed[header] = (header === 'domains'
      ? (Array.isArray(value) ? value.join(', ') : '') // Process domains -> results in string
      : (value ?? '') // Process other fields -> results in string | number | boolean
    ) as string | number | boolean; // Cast ensures TS knows the final type matches
  }
  return processed;
}

// Get Max Length for Column Width Calculation
function getMaxLength(arr: any[], key: string) {
  return Math.max(0, ...arr.map((item) => (item[key]?.toString() ?? '').length));
} // Ensure Math.max gets at least one number

export default function MarketingContactsReport() {
  const { setLabel, removeLabel } = useBreadcrumb();
  const [excludeOrganizationsInput, setExcludeOrganizationsInput] = useState('');
  const { mutateAsync, isPending, reset } = useMarketingContactsReport();
  const [reportData, setReportData] = useState<MarketingContactsResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState(''); // State for search query
  const [activeTab, setActiveTab] = useState('active'); // State for active tab

  // Memoize column definitions
  const activeContactColumns = useMemo(() => contactColDefs(), []);
  const inactiveContactColumns = useMemo(() => contactColDefs(), []);
  const oddityColumns = useMemo(() => oddityColDefs(), []);

  useEffect(() => {
    setLabel(REPORT_PATH, {
      name: 'Marketing Contacts Report',
      path: REPORT_PATH,
    });
    return () => removeLabel(REPORT_PATH);
  }, [setLabel, removeLabel]);

  // Generate Report function (fetches data only)
  const handleGenerateReport = async (e: React.FormEvent) => {
    e.preventDefault();
    reset();
    setReportData(null);
    const exclude_organizations = excludeOrganizationsInput.split('\n').map((org) => org.trim()).filter((org) => org.length > 0);

    const generationPromise = mutateAsync({ exclude_organizations });

    toast.promise(generationPromise, {
      loading: 'Generating report...',
      success: (responseData) => {
        setReportData(responseData); // Store data for grid preview
        return 'Report preview generated successfully!';
      },
      error: (err) => {
        return err instanceof Error ? err.message : 'Failed to generate report.';
      },
    });
  };

  // Download Excel function (uses existing reportData state)
  const handleDownloadExcel = () => {
    if (!reportData) {
      toast.error('No report data available to download.');
      return;
    }

    try {
      // Process data for Excel
      const processedActive = reportData.activeContacts.map((contact) => processContactDataForExcel(contact));
      const processedInactive = reportData.inactiveContacts.map((contact) => processContactDataForExcel(contact));
      const processedOddities = reportData.organizationOddities;

      // Create worksheets
      const wsActive = utils.json_to_sheet(processedActive, { header: contactHeadersExcel });
      const wsInactive = utils.json_to_sheet(processedInactive, { header: contactHeadersExcel });
      const wsOddities = utils.json_to_sheet(processedOddities, { header: oddityHeadersExcel });

      // Add formatted headers, with special case for US Bank
      utils.sheet_add_aoa(
        wsActive,
        [contactHeadersExcel.map((header) => {
          if (header === 'us_bank')
            return 'US Bank';
          if (header === 'fdic_licenses')
            return 'FDIC Licenses';
          if (header === 'holding_company_licenses')
            return 'Holding Company Licenses';
          if (header === 'other_licenses')
            return 'Other Licenses';
          if (header === 'use_restriction')
            return 'Use Restriction';
          if (header === 'use_restriction_note')
            return 'Use Restriction Note';
          return formatHeader(header);
        })],
        { origin: 'A1' },
      );
      utils.sheet_add_aoa(
        wsInactive,
        [contactHeadersExcel.map((header) => {
          if (header === 'us_bank')
            return 'US Bank';
          if (header === 'fdic_licenses')
            return 'FDIC Licenses';
          if (header === 'holding_company_licenses')
            return 'Holding Company Licenses';
          if (header === 'other_licenses')
            return 'Other Licenses';
          if (header === 'use_restriction')
            return 'Use Restriction';
          if (header === 'use_restriction_note')
            return 'Use Restriction Note';
          return formatHeader(header);
        })],
        { origin: 'A1' },
      );
      utils.sheet_add_aoa(wsOddities, [oddityHeadersExcel.map((header) => formatHeader(header))], { origin: 'A1' });

      // Auto-size columns with cap for very long text fields
      const capWidth = (header: keyof MarketingContact, width: number) =>
        (header === 'domains' || header === 'use_restriction_note') ? Math.min(width, 150) : width;

      // Active sheet widths
      const activeColWidths = contactHeadersExcel.map((header) => {
        const computed = Math.max(
          formatHeader(header).length,
          getMaxLength(processedActive, header),
        ) + 2;
        return { wch: capWidth(header, computed) };
      });
      wsActive['!cols'] = activeColWidths;

      // Inactive sheet widths
      const inactiveColWidths = contactHeadersExcel.map((header) => {
        const computed = Math.max(
          formatHeader(header).length,
          getMaxLength(processedInactive, header),
        ) + 2;
        return { wch: capWidth(header, computed) };
      });
      wsInactive['!cols'] = inactiveColWidths;

      // Create workbook and trigger download
      const wb = utils.book_new();
      utils.book_append_sheet(wb, wsActive, 'Active Contacts');
      utils.book_append_sheet(wb, wsInactive, 'Inactive Contacts');
      utils.book_append_sheet(wb, wsOddities, 'Organization Oddities');
      writeFile(wb, 'marketing_contacts_report.xlsx');

      toast.success('Excel report download started!');
    } catch (excelError) {
      console.error('Error generating Excel file:', excelError);
      toast.error('Failed to create Excel file.');
    }
  };

  return (
    <Container maxWidth={false} className="pb-12">
      <div className="mb-6">
        <h1 className="text-xl font-bold">Marketing Contacts Report</h1>
        <p className="text-sm text-muted-foreground">
          Generate a report of active/inactive contacts and organizations with missing contact types.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>
            Optionally exclude specific organizations by listing their legal names below (one per line).
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleGenerateReport}>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="excludeOrganizations">Exclude Organizations (one per line)</Label>
              <Textarea
                id="excludeOrganizations"
                value={excludeOrganizationsInput}
                onChange={(e) => setExcludeOrganizationsInput(e.target.value)}
                placeholder="Example Bank Inc.\nAnother Corp LLC"
                rows={5}
                className="mt-1"
                disabled={isPending}
              />
            </div>
          </CardContent>
          <CardFooter className="flex justify-end">
            <Button type="submit" disabled={isPending}>
              {isPending
                ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      Generating...
                    </>
                  )
                : (
                    'Generate Report'
                  )}
            </Button>
          </CardFooter>
        </form>
      </Card>

      {reportData && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Report Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="active" value={activeTab} onValueChange={setActiveTab}>
              <div className="mb-4 flex items-center justify-between gap-4">
                <TabsList>
                  <TabsTrigger value="active">
                    Active Contacts (
                    {reportData.activeContacts.length}
                    )
                  </TabsTrigger>
                  <TabsTrigger value="inactive">
                    Inactive Contacts (
                    {reportData.inactiveContacts.length}
                    )
                  </TabsTrigger>
                  <TabsTrigger value="oddities">
                    Organization Oddities (
                    {reportData.organizationOddities.length}
                    )
                  </TabsTrigger>
                </TabsList>
                <Input
                  type="search"
                  placeholder="Search preview data..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="grow"
                />
                <Button onClick={handleDownloadExcel} variant="outline" size="sm" disabled={!reportData || isPending}>
                  <FileSpreadsheet className="mr-2 size-4" />
                  Save Report
                </Button>
              </div>
              <TabsContent value="active">
                <AGDataGrid
                  domLayout="autoHeight"
                  rowData={reportData.activeContacts}
                  columnDefs={activeContactColumns}
                  quickFilterText={activeTab === 'active' ? searchQuery : ''}
                  overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No active contacts found</span>'
                />
              </TabsContent>
              <TabsContent value="inactive">
                <AGDataGrid
                  domLayout="autoHeight"
                  rowData={reportData.inactiveContacts}
                  columnDefs={inactiveContactColumns}
                  quickFilterText={activeTab === 'inactive' ? searchQuery : ''}
                  overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No inactive contacts found</span>'
                />
              </TabsContent>
              <TabsContent value="oddities">
                <AGDataGrid
                  domLayout="autoHeight"
                  rowData={reportData.organizationOddities}
                  columnDefs={oddityColumns}
                  quickFilterText={activeTab === 'oddities' ? searchQuery : ''}
                  overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No organization oddities found</span>'
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </Container>
  );
}
